// EditFoodBeverageApplication.tsx
import React, { useEffect, useState, useRef, startTransition } from 'react';
import {
  Container, Card, Box, Typography, TextField, Button, 
  Step, StepLabel, Stepper, Dialog, DialogTitle, 
  DialogContent, DialogActions, Alert, Divider, Paper, Grid
} from '@mui/material';
import toast from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
// UI Components
import Breadcrumb from 'src/ui-component/Breadcrumb';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
//import CustomModal from 'src/ui-component/customModal/CustomModal';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import EditFormfeatureViews from 'src/components/EditFormfeatureViews/EditFormfeatureViews';
import PdfGenerator from 'src/ui-component/PdfGenerator';

// Services and Redux
import { httpRequest } from 'src/services/httpRequest';
import { RootState } from 'src/redux-store/appStore';
import { sendNotification } from 'src/utils/sendNotification';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { resetFormData, updateFormData, updateAppId } from 'src/redux-store/slices/foodAndBeverageFormSlice';
//import UploadDocs from 'src/ui-component/UploadDocs';
import { getApplicationDetails } from '../common/getApplicationDetails';

// Form Components
import EstablishmentInfo from './NewApplicationForm/EstablishmentInfo';
import OwnerInfo from './NewApplicationForm/OwnerInfo';
import EstablishmentType, {EstablishmentTypeData} from './NewApplicationForm/EstablishmentType';
import AdditionalInfo from './NewApplicationForm/AdditionalInfo';
// DocumentUpload from './NewApplicationForm/DocumentUpload';
// Schema
import { FieldsSchema } from './NewApplicationForm/Schema';

// Constants
const STEP_LABELS = [
  'Establishment Information',
  'Establishment Type',
  'Owner Information',
  'Additional Information',
  // 'Document Upload',
  'Summary'
];

// Default special event form data to ensure consistency
const DEFAULT_SPECIAL_EVENT_FORM_DATA = {
  eventParticipationType: '',
  eventAddress: '',
  eventDateFrom: '',
  eventDateTo: '',
  rainDateFrom: '',
  rainDateTo: ''
};

interface LocationState {
  isDraft?: boolean;
}

// Define proper types for form data
interface SpecialEventFormData {
  status:boolean;
  eventParticipationType: string;
  eventAddress: string;
  eventDateFrom: string;
  eventDateTo: string;
  rainDateFrom: string;
  rainDateTo: string;
}

// Fee structure types
interface FeeDetails {
  applicationFee: number;
  inspectionFee: number;
}

const EditApplication = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { townshipId, permit, applicationID } = useParams();
  
  // Redux state
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const formData = useSelector((state: RootState) => state.foodBeverageFormSlice.formData);
  const appId = useSelector((state: RootState) => state.foodBeverageFormSlice.appId);
  const currentPermitRole = useSelector((state: RootState) =>
    state?.currentPermit?.currentPermitDetails?.currentPermitUserRole
  );
  const socket = useSelector((store: RootState) => store.socket.socket);
  
  // Role check
  const isManager = currentPermitRole === 'Manager';

  // Form refs for validation
  const formRefs = {
    establishmentInfo: useRef<any>({}),
    establishmentType: useRef<any>({}),
    ownerInfo: useRef<any>({}),
    additionalInfo: useRef<any>({})
    // documentUpload: useRef<any>({})
  };

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [switchTo, setSwitchTo] = useState('');
  const [feeDetails, setFeeDetails] = useState<FeeDetails>({
    applicationFee: 0,
    inspectionFee: 0
  });
  const [taxRate, setTaxRate] = useState(0);
  const [showPendingChecksDialog, setShowPendingChecksDialog] = useState(false);
  const [pendingChecks, setPendingChecks] = useState<any[]>([]);
  const [openApprovePopup, setOpenApprovePopup] = useState(false);
  const [openSendMsg, setOpenSendMsg] = useState(false);
  const messageBodyRef = useRef<string>('');

  // Helper function to get current form ref based on active step
  const getCurrentFormRef = () => {
    switch (activeStep) {
      case 0: return formRefs.establishmentInfo;
      case 1: return formRefs.establishmentType;
      case 2: return formRefs.ownerInfo;
      case 3: return formRefs.additionalInfo;
      //case 4: return formRefs.documentUpload;
      default: return null;
    }
  };

  // Method to get application fee structure
  const handleGetApplicationFee = async () => {
    setLoading(true);
    try {
      const response = await httpRequest('GET', `/configuration/fee_details?PermitID=${permit}`);
      if (response?.status === 'success') {
        const fees = response.data;
        const appFee = fees.find((f: any) => f.FeeTypeName === 'Application Fee');
        const inspectFee = fees.find((f: any) => f.FeeTypeName === 'Inspection Fee');
        const creditFee = fees.find((f: any) => f.FeeTypeName === 'Credit Card Fee');

        setFeeDetails({
          applicationFee: parseFloat(appFee?.FeeValue || '0'),
          inspectionFee: parseFloat(inspectFee?.FeeValue || '0')
        });
        setTaxRate(parseFloat(creditFee?.FeeValue || '0'));
      } else {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
      }
    } catch (error) {
      dispatch(handleError("Failed to fetch application fees"));
    } finally {
      setLoading(false);
    }
  };

  // Fetch application details
const fetchApplicationDetails = async () => {
  setLoading(true);
  const { isDraft } = (location.state as LocationState) || {};
  const response = await getApplicationDetails({ applicationID, permit, isDraft });
  
  if (response?.status === 'success') {
    const appData = response?.data?.data[0];

    // Initialize establishment type data structure
    let establishmentTypeData = {};
    
    try {
      // Handle EstablishmentType from API response
      if (appData.EstablishmentType && typeof appData.EstablishmentType === 'object') {
        // Create a normalized copy of the establishment type data
        establishmentTypeData = { ...appData.EstablishmentType };
        
        // Special handling for "Special Event" data - convert API field names to component field names
        if (establishmentTypeData['Special Event']) {
          const apiSpecialEvent = establishmentTypeData['Special Event'];
          establishmentTypeData['Special Event'] = {
            // Convert API fields to the component's expected format
            status: apiSpecialEvent.Status,
            eventParticipationType: apiSpecialEvent.EventParticipationType,
            eventAddress: apiSpecialEvent.EventAddress,
            eventDateFrom: apiSpecialEvent.EventDateFrom,
            eventDateTo: apiSpecialEvent.EventDateTo,
            rainDateFrom: apiSpecialEvent.RainDateFrom,
            rainDateTo: apiSpecialEvent.RainDateTo
          };
        }
        
        // For other establishment types, ensure consistency
        Object.keys(establishmentTypeData).forEach(type => {
          if (type !== 'Special Event' && establishmentTypeData[type]) {
            // Ensure each type has the expected structure
            const typeData = establishmentTypeData[type];
            establishmentTypeData[type] = {
              id: typeData.id || null,
              size: typeData.size || '',
              fee: typeData.fee || null,
              [type]: typeData[type] === true || typeData.status === true
            };
          }
        });
      }
    } catch (e) {
    }
    
    // Format the application data for our form
    const formattedData = {
      applicationID: appData.ApplicationID,
      applicationNumber: appData.ApplicationNumber,
      applicationStatus: appData.ApplicationStatus,
      userID: appData.UserID,
      
      // Establishment Info
      establishmentName: appData.EstablishmentName || '',
      establishmentEmail: appData.EstablishmentEmail || '',
      establishmentPhone: appData.EstablishmentPhone || '',
      establishmentAddress: appData.EstablishmentAddress || '',
      establishmentTown: appData.EstablishmentTown || '',
      establishmentContactName: appData.EstablishmentContactName || '',
      blockLot: appData.BlockLot || '',
      
      // Owner Info
      applicantName: appData.ApplicantName || '',
      applicantAddress: appData.ApplicantAddress || '',
      applicantSuitePOBox: appData.ApplicantSuite || '',
      applicantCity: appData.ApplicantCity || '',
      applicantState: appData.ApplicantState || '',
      applicantZipCode: appData.ApplicantZipCode || '',
      applicantEmail: appData.ApplicantEmail || '',
      applicantPhone: appData.ApplicantPhone || '',
      
      // Establishment Type
      EstablishmentType: establishmentTypeData,
      
      // Additional Info
      hasSecondaryContactInfo: appData.HasSecondaryContact || false,
      secondaryContactName: appData.SecondaryContactName || '',
      secondaryDepartment: appData.SecondaryDepartment || '',
      secondaryAddress: appData.SecondaryAddress || '',
      secondaryEmail: appData.SecondaryEmail || '',
      secondaryPhone: appData.SecondaryPhone || '',

      //Document Upload
      // DocumentFileUpload: formData.DocumentFileUpload ? formData.DocumentFileUpload.map(doc => ({
      //   Type: doc.type || 'document',
      //   FileName: doc.name || ''
      // })) : []
      
    };
    
    // Update Redux store
    dispatch(updateFormData(formattedData));
    dispatch(updateAppId({ applicationId: appData.ApplicationID }));
    
    // Handle existing document files
    if (appData.DocumentFileUpload) {
      try {
        const existingFiles = JSON.parse(appData.DocumentFileUpload);
        if (Array.isArray(existingFiles)) {
          dispatch(updateFormData({ document: existingFiles }));
        }
      } catch (e) {
      }
    }
  } else {
    toast.error(response?.message || 'Failed to load application data');
    dispatch(handleError(response?.message || 'Failed to load application data'));
  }
  
  setLoading(false);
};

  // Initialize component
  useEffect(() => {
     if (!applicationID) return;
    fetchApplicationDetails();
    handleGetApplicationFee();
    
    // Cleanup on unmount
    return () => {
      dispatch(resetFormData());
    };
  }, [applicationID, dispatch]);

  // Define handleStepSubmit for form validation
  const handleStepSubmit = async (): Promise<boolean> => {
    const currentFormRef = getCurrentFormRef();
    if (currentFormRef?.current) {
      try {
        if (currentFormRef.current.submitForm) {
          await currentFormRef.current.submitForm();
        }
        if (currentFormRef.current.validateForm) {
          const errors = await currentFormRef.current.validateForm();
          return Object.keys(errors).length === 0;
        }
      } catch (error) {
        return false;
      }
    }
    return true; // If no form ref (e.g., Summary step), allow proceeding
  };

  // Handle establishment type changes
  const handleEstablishmentTypeChange = (type: string, checked: boolean) => {
    const updatedEstablishmentType = { ...formData.EstablishmentType };
  
    if (type === 'Special Event') {
      if (!updatedEstablishmentType['Special Event'] || typeof updatedEstablishmentType['Special Event'] !== 'object') {
        updatedEstablishmentType['Special Event'] = {
          Status: checked,
          EventParticipationType: '',
          EventAddress: '',
          EventDateFrom: '',
          EventDateTo: '',
          RainDateFrom: '',
          RainDateTo: ''
        };
      } else {
        updatedEstablishmentType['Special Event'] = {
          ...updatedEstablishmentType['Special Event'],
          status: checked
        };
      }
    } else {
      if (!updatedEstablishmentType[type]) {
        updatedEstablishmentType[type] = {
          id: null,
          size: '',
          fee: null,
          [type]: checked
        };
      } else {
        updatedEstablishmentType[type] = {
          ...updatedEstablishmentType[type],
          [type]: checked
        };
  
        // Optional: clear size data if unchecked
        if (!checked) {
          updatedEstablishmentType[type].id = null;
          updatedEstablishmentType[type].size = '';
          updatedEstablishmentType[type].fee = null;
        }
      }
    }
  
    dispatch(updateFormData({ EstablishmentType: updatedEstablishmentType }));
  };  

  const handleSpecialEventChange = (fieldName, value) => {
    const updatedEstablishmentType = { ...formData.EstablishmentType };
    if (!updatedEstablishmentType['Special Event']) {
      updatedEstablishmentType['Special Event'] = {
        status: false,
        eventParticipationType: '',
        eventAddress: '',
        eventDateFrom: '',
        eventDateTo: '',
        rainDateFrom: '',
        rainDateTo: ''
      };
    }
    
    // Update the specific field
    updatedEstablishmentType['Special Event'] = {
      ...updatedEstablishmentType['Special Event'],
      [fieldName]: value
    };
    
    dispatch(updateFormData({ EstablishmentType: updatedEstablishmentType }));
  };

  const preparePayload = () => {
  
      // Get establishment type data
      const establishmentTypeData = formData.EstablishmentType || DEFAULT_SPECIAL_EVENT_FORM_DATA;
  
      // Build establishment type payload
      const buildEstablishmentTypePayload = () => {
        const result = {};
        
        // Process regular establishment types
        Object.entries(establishmentTypeData).forEach(([type, data]) => {
          if (type === 'Special Event') {
            // Special event has a different structure
            const specialEventData = data as SpecialEventFormData;
            result[type] = {
              Status: specialEventData.status,
              EventParticipationType: specialEventData.eventParticipationType || '',
              EventAddress: specialEventData.eventAddress || '',
              EventDateFrom: specialEventData.eventDateFrom || '',
              EventDateTo: specialEventData.eventDateTo || '',
              RainDateFrom: specialEventData.rainDateFrom || '',
              RainDateTo: specialEventData.rainDateTo || ''
            };
          } else {
            // Regular establishment types
            const typeData = data as EstablishmentTypeData;
            const apiKeyName = type; // Same as the type name
            
            result[apiKeyName] = {
              [apiKeyName]: !!typeData[type],
              id: typeData.id || null,
              size: typeData.size || null,
              status: false
            };
          }
        });
        
        return result;
      };
  
      return {
        // Establishment Information
        EstablishmentName: formData.establishmentName || '',
        EstablishmentEmail: formData.establishmentEmail || '',
        EstablishmentPhone: formData.establishmentPhone || '',
        EstablishmentAddress: formData.establishmentAddress || '',
        EstablishmentTown: formData.establishmentTown || '',
        BlockLot: formData.blockLot || '',
        EstablishmentContactName: formData.establishmentContactName || '',
        
        // Owner Information
        ApplicantName: formData.applicantName || '',
        ApplicantAddress: formData.applicantAddress || '',
        ApplicantSuite: formData.applicantSuitePOBox || '',
        ApplicantCity: formData.applicantCity || '',
        ApplicantState: formData.applicantState || '',
        ApplicantZipCode: formData.applicantZipCode || '',
        ApplicantEmail: formData.applicantEmail || '',
        ApplicantPhone: formData.applicantPhone || '',
        
        // Establishment Type
        EstablishmentType: buildEstablishmentTypePayload(),
        
        // Additional Information
        HasSecondaryContactInfo: formData.hasSecondaryContactInfo || false,
        SecondaryContactName: formData.secondaryContactName || '',
        SecondaryDepartment: formData.secondaryDepartment || '',
        SecondaryAddress: formData.secondaryAddress || '',
        SecondaryEmail: formData.secondaryEmail || '',
        SecondaryPhone: formData.secondaryPhone || '',
    
        //Document Upload
        // DocumentFileUpload: formData.DocumentFileUpload ? formData.DocumentFileUpload.map(doc => ({
        //   Type: doc.Type || 'document',
        //   FileName: doc.FileName || ''
        // })) : []

   };
  };

  // Handle update application
  const handleUpdateApplication = async () => {
    setLoading(true);
    try {
      const appIdToUse = appId || applicationID;
       const payload = preparePayload();
    
    // Debug: Log the payload to see what's being sent
    // eslint-disable-next-line no-console
    console.log('Update Payload:', {
      ...payload,
      //DocumentFileUpload: payload.DocumentFileUpload
    });
      const response = await httpRequest(
        'PUT',
        `/food_beverage/update/${appIdToUse}`,
        preparePayload()
      );
      
      if (response?.status === 'success') {
        toast.success('Application updated successfully.');

         // eslint-disable-next-line no-console
         console.log('Update Response:', response);
         
        const status = formData?.applicationStatus ;
        if (status === 'In Review' || status ==='Approved' || status === 'Rejected') {
           navigate(`/${townshipId}/Department of Health and Mental Hygiene/${permit}/application-list`);   
        }else {
          // Default fallback
         navigate(`/${townshipId}/Department of Health and Mental Hygiene/${permit}/draft-application`);
        }
        return true;
      } else {
        toast.error(response?.message || 'Failed to update application');
        dispatch(handleError(response?.message));
        return false;
      }
    } catch (error) {
      dispatch(handleError("Failed to update application"));
      return false;
    } finally {
      setLoading(false);
    }
  };

   // Handle size selection
   const handleSizeSelection = (type, sizeId, sizeName, fee) => {
    const updatedEstablishmentType = { ...formData.EstablishmentType };
    if (!updatedEstablishmentType[type]) {
      updatedEstablishmentType[type] = {
        id: sizeId,
        size: sizeName,
        fee: fee,
        [type]: true
      };
    } else {
      updatedEstablishmentType[type] = {
        ...updatedEstablishmentType[type],
        id: sizeId,
        size: sizeName,
        fee: fee
      };
    }
    
    dispatch(updateFormData({ EstablishmentType: updatedEstablishmentType }));
  };

  // Handle submit application
  const handleSubmit = async () => {
    // First update the application
    const updateSuccess = await handleUpdateApplication();
    if (!updateSuccess) return;
    setLoading(true);
    try {
      const appIdToUse = appId || applicationID;
      const payload = {
        ApplicationID: appIdToUse,
        ApplicationStatus: 'In Review',
        PermitID: Number(permit),
        PermitPrefix: 'FB',
       };
      
      const response = await httpRequest('POST', '/payments/update_status', payload);
      
      if (response?.status === 'success') {
        toast.success('Application submitted successfully.');
        navigate(`/${townshipId}/Department of Health and Mental Hygiene/${permit}/application-list`);
      } else {
        toast.error(response?.message || 'Failed to submit application');
      }
    } catch (error) {
      dispatch(handleError("Failed to submit application"));
    } finally {
      setLoading(false);
    }
  };

  // Handle approve application (for managers)
  const handleApprove = async () => {
    setOpenApprovePopup(false);

      // First update the application
  const updated = await handleUpdateApplication();
  if (!updated) return;
    setLoading(true);

    const appIdToUse = appId || applicationID;
    const payload = {
       ApplicationID: Number(appIdToUse),
       ApplicationStatus:'Approved',
       Address: formData?.establishmentAddress,
       BlockLot: formData?.blockLot || '' // Add BlockLot to the payload
    }
    
    try {
      
      const response = await httpRequest(
        'PUT',
        `/applications/${permit}/update_status`,
        payload
      );
      
      if (response?.status === 'success') {
        // Send notification to user
        if (formData.userID && formData.applicationNumber) {
          sendNotification(socket, {
            UserID: formData.userID,
            ApplicationID: appIdToUse,
            PermitID: permit,
            TownshipID: townshipId,
            Title: 'Application Approved',
            Message: `Application no. ${formData.applicationNumber} is approved successfully.`,
            ApplicationNumber: formData.applicationNumber
          });
        }
        
        toast.success('Application approved successfully.');
        navigate(`/${townshipId}/Department of Health and Mental Hygiene/${permit}/application-list`);
      } else {
        toast.error(response?.message || 'Failed to approve application');
      }
    } catch (error) {
      dispatch(handleError("Failed to approve application"));
    } finally {
      setLoading(false);
    }
  };

   // Function to navigate to payment page
   const proceedToPaymentPage = () => {
    const appIdToUse = appId || applicationID;
    
    const paymentSummary = {
      fees: Object.fromEntries(
        Object.entries(feeDetails).filter(([, value]) => value > 0)
      ),
      ...(taxRate > 0 ? { taxRate } : {})
    };
    
    const paymentApplicationDetails = {
      applicationDetails: {
        userName: `${userData?.FirstName} ${userData?.LastName}`,
        applicationNumber: formData.ApplicationNumber || null,
        applicationId: appIdToUse,
        applicationStatus: formData.applicationStatus || 'Draft',
        statusPayload: 'In Review',
        permitPrefix: 'FB',
        redirectUrl: `/${townshipId}/Department of Health and Mental Hygiene/${permit}/application-list`
      }
    };
    
    navigate(`/payment-methods`, {
      state: { paymentSummary, paymentApplicationDetails }
    });
  };

  // Handle proceed to payment if needed
  const handleProceedToPayment = async () => {
    const appIdToUse = appId || applicationID;
    
    // Check for any pending requirements
    setIsChecking(true);
    
    try {
      const checkResponse = await httpRequest(
        'GET',
        `/food_beverage/${appIdToUse}/check_requirements_status`
      );
      
      if (checkResponse?.status === 'success') {
        // No pending requirements or checks needed
        if (checkResponse.message === 'No pending requirements' || 
            (Array.isArray(checkResponse.data) && checkResponse.data.length === 0)) {
          // Proceed with payment
          proceedToPaymentPage();
        } else {
          // Has pending requirements
          setPendingChecks(checkResponse.data);
          setShowPendingChecksDialog(true);
        }
      } else {
        // Assume no blocking issues if the check endpoint isn't implemented yet
        proceedToPaymentPage();
      }
    } catch (error) {
      // For now, if the endpoint doesn't exist, continue to payment
      proceedToPaymentPage();
    } finally {
      setIsChecking(false);
    }
  };
  // Handle cancel - return to application list
  const handleCancel = () => {
    navigate(`/${townshipId}/Department of Health and Mental Hygiene/${permit}/application-list`);
  };
  /*const handleDocumentSubmit = (values) => {
      // This function will be passed to the DocumentUpload component
      // // The component may return the files in the values object
      // if (values?.documents) {
      //   dispatch(updateFormData({ DocumentFileUpload: values.documents }));
      // }
       if (values?.documents) {
      const normalizedDocuments = values.documents.map(doc => ({
      Type: doc.Type || doc.type || 'document',
      FileName: doc.FileName || doc.name || doc.filename || '',
      // Preserve any additional properties
      ...(doc.id && { id: doc.id }),
      ...(doc.size && { size: doc.size }),
      ...(doc.url && { url: doc.url }),
      ...(doc.file && { file: doc.file }) // For new file uploads
    }));
    
    dispatch(updateFormData({ DocumentFileUpload: normalizedDocuments }));
  }
};*/

  // Unified button action handler
  const handleButtonAction = async (type: string) => {
    switch (type) {
      case 'Next': {
        const isValid = await handleStepSubmit();
        if (isValid) {
          setActiveStep((prev) => prev + 1);
        } else {
          toast.error('Please fix the errors before proceeding');
        }
        break;
      }
      case 'Back':
        setActiveStep((prev) => prev - 1);
        break;
      case 'Proceed to Payment':
        handleProceedToPayment();
        break;
      case 'Update':{
        //setClickedOn('Update');
        const isValid = await handleStepSubmit();
        if (isValid) {
          await handleUpdateApplication();
          navigate(`/${townshipId}/Department of Health and Mental Hygiene/${permit}/application-list`);
        }
        break;
      }
      case 'Submit':{
        //setClickedOn('Submit');
        const isValidForSubmit = await handleStepSubmit();
        if (isValidForSubmit) {
          await handleSubmit();
        }
        break;
      }
      case 'Approve': {
        //setClickedOn('Approve');
        const isValidForApprove = await handleStepSubmit();
        if (isValidForApprove) {
          setOpenApprovePopup(true);
        }
        break;
      }
      case 'Save Draft':{
        const isValidForDraft = await handleStepSubmit();
        if (isValidForDraft) {
          await handleUpdateApplication();
        }
        break;
      }
      case 'Cancel':{
        handleCancel();
        break;
      }
      default:
        break;
    }
  };

const FeeSummarySection = () => {
  const totalFee = feeDetails.applicationFee + feeDetails.inspectionFee;

  return (
    <Grid container spacing={4} justifyContent="center">
      <Grid item xs={12} md={6}>
        <Paper
          elevation={1}
          sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: '#f9fafb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          {/* Fees */}
          {[ 
            { label: 'Application Fee', value: `$${feeDetails.applicationFee.toFixed(2)}` },
            { label: 'Inspection Fee', value: `$${feeDetails.inspectionFee.toFixed(2)}` },
            ...(taxRate > 0
              ? [{ label: 'Credit Card Processing Fee', value: `${taxRate}%` }]
              : [])
          ].map((item, index) => (
            <React.Fragment key={item.label}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography>{item.label}</Typography>
                <Typography>{item.value}</Typography>
              </Box>
              {index < 2 && <Divider />}
            </React.Fragment>
          ))}

          <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

          {/* Total */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">
              Total Fee
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="primary">
              ${totalFee.toFixed(2)}
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};
 // Define steps for stepper
  const steps = [
    {
      label: 'Establishment Information',
      Component: (
        <EstablishmentInfo
          ref={formRefs.establishmentInfo}
          formData={formData}
          setFieldValue={(field: string, value: any) => dispatch(updateFormData({ [field]: value }))}
          handleSubmit={() => {}}
          isButtonHide={true}
        />
      )
    },
     {
      label: 'Establishment Type',
      Component: (
        <EstablishmentType
          
                   ref={formRefs.establishmentType}
                   establishmentData={formData.EstablishmentType || DEFAULT_SPECIAL_EVENT_FORM_DATA}
                   handleEstablishmentTypeChange={handleEstablishmentTypeChange}
                   handleSizeSelection={handleSizeSelection}
                   handleSpecialEventChange={handleSpecialEventChange}
                   handleSubmit={() => dispatch(updateFormData({ DocumentFileUpload: [] }))}
                   errors={{}}
                   touched={{}}
        />
      )
    },
    {
      label: 'Owner Information',
      Component: (
        <OwnerInfo
          ref={formRefs.ownerInfo}
          formData={formData}
          setFieldValue={(field: string, value: any) => dispatch(updateFormData({ [field]: value }))}
          handleSubmit={() => {}}
          isButtonHide={true}
        />
      )
    },
    {
      label: 'Additional Information',
      Component: (
        <AdditionalInfo
          ref={formRefs.additionalInfo}
          formData={formData}
          setFieldValue={(field: string, value: any) => dispatch(updateFormData({ [field]: value }))}
          handleSubmit={() => {}}
          isButtonHide={true}
        />
      )
    },
    // {
    //   label: 'Upload Documents',
    //   Component: (
    //     <DocumentUpload
    //     ref={formRefs.documentUpload}
    //     formData={{ DocumentFileUpload: formData?.DocumentFileUpload || [] }}
    //     isEdit={true}
    //     ApplicationId={applicationID || appId}
    //     permitName="Food and Beverage"
    //     isButtonHide={true}
    //     handleSubmit={handleDocumentSubmit}
    //   />
    //   )
    // },
    {
      label: 'Summary',
      Component: (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: '1200px', mx: 'auto' }}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, md: 4 },
              mb: 4,
              borderRadius: 3,
              backgroundColor: '#ffffff',
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                textAlign: 'center',
                mb: 3,
              }}
            >
              Application Summary
            </Typography>
    
            <Alert
              severity="info"
              icon={false}
              sx={{
                fontWeight: 600,
                backgroundColor: '#e8f0fe',
                color: '#1f2937',
                textAlign: 'center',
                mb: 4,
                py: 2,
                justifyContent:'center'
              }}
            >
              Please review your application details before proceeding.
            </Alert>
    
            <FeeSummarySection />
          </Paper>
        </Box>
      )
    }
    
  ];

  // Final step buttons based on application status
const getStepperButtons = () => {
  const status = formData?.applicationStatus;
  const isLastStep = activeStep === STEP_LABELS.length - 1;
  
  let finalButtons = [];
  
  // Final step buttons based on application status
  if (isLastStep) {
    if (status === 'Draft') {
      finalButtons = [
        {
          label: 'Cancel',
          function: () => handleButtonAction('Cancel'),
          color: 'error',
          variant: 'outlined'
        },
        {
          label: 'Update',
          function: () => handleButtonAction('Update'),
          color: 'primary',
          variant: 'outlined'
        },
        {
          label:  isChecking ? 'Checking Status...' : 'Proceed to Payment',
          function: () => handleButtonAction('Proceed to Payment'),
          color: 'primary',
          variant: 'contained',
          disabled: isChecking
        }
      ];
    } else if (status === 'In Review' && isManager) {
      finalButtons = [
        {
          label: 'Cancel',
          function: () => handleButtonAction('Cancel'),
          color: 'error',
          variant: 'outlined'
        },
        {
          label: 'Update',
          function: () => handleButtonAction('Update'),
          color: 'primary',
          variant: 'outlined'
        },
        {
          label: 'Approve',
          function: () => handleButtonAction('Approve'),
          color: 'success',
          variant: 'contained'
        }
      ];
    } else {
      finalButtons = [
        {
          label: 'Cancel',
          function: () => handleButtonAction('Cancel'),
          color: 'error',
          variant: 'outlined'
        },
        {
          label: 'Update',
          function: () => handleButtonAction('Update'),
          color: 'primary',
          variant: 'contained'
        }
      ];
    }
  } else {
    // Non-final step buttons
    finalButtons = [
      {
        label: 'Back',
        function: () => handleButtonAction('Back'),
        color: 'primary',
        variant: 'outlined',
        disabled: activeStep === 0
      },
      {
        label: 'Save Draft',
        function: () => handleButtonAction('Save Draft'),
        color: 'primary',
        variant: 'outlined'
      },
      {
        label: 'Next',
        function: () => handleButtonAction('Next'),
        color: 'primary',
        variant: 'contained'
      }
    ];
  }
  
  return finalButtons;
};

// Handle message sending for notifications
const handleSendMessage = () => {
  if (!messageBodyRef.current.trim()) {
    toast.error('Please enter a message');
    return;
  }
  
  setLoading(true);
  const appIdToUse = appId || applicationID;
  
  httpRequest('POST', '/email/send', {
    //UserID: formData.UserID,
    ApplicationID: appIdToUse,
    PermitID: permit,
    //TownshipID: townshipId,
    title: 'Message from Manager',
    message: messageBodyRef.current
  })
    .then((res) => {
      if (res?.status === 'success') {
        toast.success('Message sent successfully');
        setOpenSendMsg(false);
        messageBodyRef.current = '';
      } else {
        toast.error(res?.message || 'Failed to send message');
      }
    })
    .catch(() => {
      dispatch(handleError("Failed to send message"));
    })
    .finally(() => {
      setLoading(false);
    });
};

return loading ? (
  <LoadingOverlay />
) : (
  <Container sx={{ mt: 2, pb: 4, px: {xs: 1, md: "auto"} }} className="main-table-container">
    <Breadcrumb PermitName="Food and Beverage" PageTitle="Edit Application" />
    <FormHeaderGreeting
      name={`${userData?.FirstName} ${userData?.LastName}`}
      applicationType="edit"
      applicationStatus={formData?.applicationStatus}
      applicationNumber={formData?.applicationNumber}
    />
     {switchTo !== '' ? (
        <EditFormfeatureViews
          key={switchTo} 
          switchTo={switchTo}
          setSwitchTo={setSwitchTo}
          applicationDetails={{
            applicationID: applicationID,
            TownshipID: townshipId,
            UserID: formData.UserID,
            ApplicationStatus:formData?.applicationStatus
          }}
        />
      ) : (

    <Card>
      <Box m={{ md: 3, xs: 1 }} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">
          Edit Application {formData?.ApplicationNumber ? `#${formData.ApplicationNumber}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
           <Button variant="outlined" onClick={async () => {
              const isValid = await handleStepSubmit();
              if (isValid) { startTransition(() => setSwitchTo('Upload/View Document'));
              } else {
              toast.error('Please fix the errors before switching views');}}}
              //disabled={['Draft', 'Pending'].includes(formData?.applicationStatus)}
              >Upload/View Document</Button>
           {isManager && (
           <>
             <Button variant="outlined" onClick={() => 
              setOpenSendMsg(true)}
              disabled={['Draft', 'Pending'].includes(formData?.applicationStatus)}
              >Send Message</Button>
             <Button variant="outlined" onClick={async () => {
              const isValid = await handleStepSubmit();
              if (isValid) { startTransition(() => setSwitchTo('View Message History'));
              } else {
              toast.error('Please fix the errors before switching views');}}}
              disabled={['Draft', 'Pending'].includes(formData?.applicationStatus)}
              >View Message History</Button>
             <Button variant="outlined" onClick={async () => {
              const isValid = await handleStepSubmit();
              if (isValid) { startTransition(() =>setSwitchTo('View Payment History'));
              } else {
              toast.error('Please fix the errors before switching views');}}}
              disabled={['Draft', 'Pending'].includes(formData?.applicationStatus)}
              >View Payment History</Button>
           </>
          )}
          <PdfGenerator PermitName="Food and Beverage" allFields={FieldsSchema(formData)} />
        </Box>
      </Box>

      <Box m={{ md: 3, xs: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEP_LABELS.map((label, index) => (
            <Step key={label}>
              <StepLabel
                onClick={() => {
                  setSwitchTo(index.toString());
                  
                  // Check if current form is valid before switching
                  handleStepSubmit().then((isValid) => {
                    if (isValid) {
                      setActiveStep(index);
                    } else {
                      toast.error('Please fix the errors before switching steps');
                    }
                    setSwitchTo('');
                  });
                }}
                sx={{ cursor: 'pointer' }}
              >
                {label}
                {switchTo === index.toString() && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 3 }}>
          {steps[activeStep].Component}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Box>
            {activeStep > 0 && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleButtonAction('Back')}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Render different buttons based on step */}
            {getStepperButtons().map((button, index) => (
              <Button
                key={index}
                variant={button.variant}
                color={button.color}
                onClick={button.function}
                disabled={button.disabled}
              >
                {button.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>
    </Card>
    )}

    {/* Send Message Dialog */}
    <Dialog open={openSendMsg} onClose={() => setOpenSendMsg(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h4">Send Message to Applicant</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message"
            placeholder="Enter your message to the applicant"
            onChange={(e) => { messageBodyRef.current = e.target.value; }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSendMsg(false)} color="primary">Cancel</Button>
          <Button onClick={handleSendMessage} variant="contained" color="primary">Send Message</Button>
        </DialogActions>
      </Dialog>

    {/* Approve Application Dialog */}
    <Dialog open={openApprovePopup} onClose={() => setOpenApprovePopup(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h4">Confirm Approval</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Alert severity="info" sx={{ mt: 2 }}>
          Are you sure you want to approve this application?
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenApprovePopup(false)} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApprove} variant="contained" color="success">
          Approve Application
        </Button>
      </DialogActions>
    </Dialog>

    {/* Pending Checks Dialog */}
    <Dialog
      open={showPendingChecksDialog}
      onClose={() => setShowPendingChecksDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h4" color="error">
          Cannot Proceed to Payment
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" sx={{ mt: 2 }}>
            You must fulfill all requirements before proceeding to payment.
          </Alert>
          <br />
          <Typography variant="body1">
            You have pending requirements that need to be completed:
          </Typography>
        </Box>

        {pendingChecks.map((check, index) => (
          <Box
            key={index}
            sx={{ mb: 3, p: 2, bgcolor: '#fff4e5', borderRadius: 1 }}
          >
            <Typography fontWeight="bold">
              {check.name} - {check.status}
            </Typography>
            <Typography variant="body2">{check.description}</Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setShowPendingChecksDialog(false)}
          variant="contained"
        >
          OK, I'll Complete These First
        </Button>
      </DialogActions>
    </Dialog>

    {/* Form Status Views */}
    {/* {!activeStep && (<EditFormfeatureViews
        switchTo={switchTo}
        setSwitchTo={setSwitchTo}
        applicationDetails={formData}
      />)} */}
  </Container>
);
};

export default EditApplication;